/**
 * Generic press-and-hold detector for touch + mouse + pen.
 *
 * Fires `onLongPress(event)` after `delayMs` has elapsed since pointerdown,
 * provided the pointer hasn't moved more than `moveTolerancePx` away from
 * the initial position and hasn't released early. Same shape the chat
 * input's mic button uses, factored for reuse.
 *
 * Returns event handlers — spread onto any element. Pairs with the
 * element's own `onClick` (which still fires on quick tap-release).
 *
 * Edge cases handled:
 *   - Scroll cancels the long-press (touchmove past tolerance)
 *   - Pointer leaving the element cancels (pointercancel + pointerleave)
 *   - Right-click / non-primary pointer doesn't trigger
 *   - Synthetic clicks fired right after a long-press are suppressed via
 *     `triggeredRef` so the row's onClick doesn't navigate after a long-press
 *     that opened the action menu
 */

import { useCallback, useEffect, useRef } from 'react';

export interface LongPressOptions {
  /** Milliseconds before the press qualifies as a long-press. Default 500ms. */
  delayMs?: number;
  /** Max pointer movement (px) before the press is cancelled. Default 8px. */
  moveTolerancePx?: number;
  /** Called when the long-press threshold fires. */
  onLongPress: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface LongPressBindings {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerLeave: (e: React.PointerEvent<HTMLElement>) => void;
  /** True once the long-press has fired for the current gesture. Consumers
   *  should check this in onClick and bail out so the long-press doesn't
   *  also trigger the click action. Resets on the next pointerdown. */
  wasLongPressTriggeredRef: React.MutableRefObject<boolean>;
}

export function useLongPress({
  delayMs = 500,
  moveTolerancePx = 8,
  onLongPress,
}: LongPressOptions): LongPressBindings {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  const triggeredRef = useRef<boolean>(false);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!e.isPrimary || e.button !== 0) return;
      triggeredRef.current = false;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      cancel();
      const event = e; // capture reference for the closure
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        triggeredRef.current = true;
        onLongPress(event);
      }, delayMs);
    },
    [cancel, delayMs, onLongPress],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (timerRef.current === null) return;
      const dx = Math.abs(e.clientX - startXRef.current);
      const dy = Math.abs(e.clientY - startYRef.current);
      if (dx > moveTolerancePx || dy > moveTolerancePx) cancel();
    },
    [cancel, moveTolerancePx],
  );

  const onPointerUp = useCallback(() => cancel(), [cancel]);
  const onPointerCancel = useCallback(() => cancel(), [cancel]);
  const onPointerLeave = useCallback(() => cancel(), [cancel]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    wasLongPressTriggeredRef: triggeredRef,
  };
}
